import React, { useState, useRef } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
    Box, Button, Typography, LinearProgress, Chip,
    Dialog, DialogTitle, DialogContent, DialogActions,
    Snackbar, Alert,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import Apiurl from "../../Helpers/apiUrl";

const CHUNK_SIZE = 50;

const TEMPLATE_COLUMNS = [
    "hsnCode", "sku_code", "brand", "product_desc", "finish",
    "mrp", "gst", "category", "priceSegment", "spare_fg",
    "product_typ_group", "collection",
];

const DUMMY_ROW = {
    hsnCode: "8481", sku_code: "GR-23218-000", brand: "GROHE",
    product_desc: "GROHE Eurosmart Single-Lever Basin Mixer", finish: "Chrome",
    mrp: "12500", gst: "18", category: "Faucets", priceSegment: "Premium",
    spare_fg: "FG", product_typ_group: "Basin Mixer", collection: "Eurosmart",
};

const VALIDATIONS = {
    sku_code: { required: true, label: "SKU Code" },
    brand: { required: true, label: "Brand", allowedValues: ["GROHE", "AS"] },
    product_desc: { required: true, label: "Description" },
    mrp: { required: true, label: "MRP", isNumber: true, min: 0 },
    gst: { required: false, label: "GST", isNumber: true, min: 0, max: 100 },
    hsnCode: { required: false, label: "HSN Code" },
    finish: { required: true, label: "Finish" },
    category: { required: false, label: "Category" },
    priceSegment: { required: true, label: "Price Segment" },
    spare_fg: { required: false, label: "Spare FG" },
    product_typ_group: { required: false, label: "Product Type Group" },
    collection: { required: true, label: "Collection" },
};

const validateRow = (row, rowIndex) => {
    const errors = [];
    for (const [field, rules] of Object.entries(VALIDATIONS)) {
        const value = row[field];
        const isEmpty = value === undefined || value === null || String(value).trim() === "";

        if (rules.required && isEmpty) {
            errors.push(`Row ${rowIndex + 1}: "${rules.label}" is required.`);
            continue;
        }
        if (isEmpty) continue;

        if (rules.isNumber) {
            const num = Number(value);
            if (isNaN(num)) {
                errors.push(`Row ${rowIndex + 1}: "${rules.label}" must be a number.`);
            } else {
                if (rules.min !== undefined && num < rules.min)
                    errors.push(`Row ${rowIndex + 1}: "${rules.label}" must be ≥ ${rules.min}.`);
                if (rules.max !== undefined && num > rules.max)
                    errors.push(`Row ${rowIndex + 1}: "${rules.label}" must be ≤ ${rules.max}.`);
            }
        }

        if (rules.allowedValues && !rules.allowedValues.includes(String(value).trim())) {
            errors.push(`Row ${rowIndex + 1}: "${rules.label}" must be one of: ${rules.allowedValues.join(", ")}.`);
        }
    }
    return errors;
};

export default function BulkUploadProduct() {
    const [open, setOpen] = useState(false);
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [totalRows, setTotalRows] = useState(0);
    const [uploadedRows, setUploadedRows] = useState(0);
    const [results, setResults] = useState(null);
    const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
    const fileInputRef = useRef();

    const showSnack = (message, severity = "error") => {
        setSnack({ open: true, message, severity });
    };

    const handleOpen = () => {
        setFile(null);
        setProgress(0);
        setUploadedRows(0);
        setTotalRows(0);
        setResults(null);
        setOpen(true);
    };

    const handleClose = () => {
        if (uploading) return;
        setOpen(false);
    };

    const handleDownloadTemplate = () => {
        const worksheet = XLSX.utils.json_to_sheet([DUMMY_ROW], { header: TEMPLATE_COLUMNS });
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Products");
        const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        saveAs(new Blob([buffer], { type: "application/octet-stream" }), "BulkUpload_Template.xlsx");
    };

    const handleFileChange = (e) => {
        const selected = e.target.files[0];
        if (!selected) return;
        const ext = selected.name.split(".").pop().toLowerCase();
        if (!["xlsx", "xls"].includes(ext)) {
            showSnack("Please upload a .xlsx or .xls file.", "error");
            return;
        }
        setFile(selected);
        setResults(null);
        setProgress(0);
        setUploadedRows(0);
    };

    const handleUpload = async () => {
        if (!file) return;

        const arrayBuffer = await file.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: "array" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });

        if (rows.length === 0) {
            showSnack("The uploaded file has no data rows.", "warning");
            return;
        }

        // validate all rows first
        const allErrors = [];
        rows.forEach((row, i) => {
            const errs = validateRow(row, i);
            allErrors.push(...errs);
        });

        if (allErrors.length > 0) {
            const display = allErrors.slice(0, 5);
            const extra = allErrors.length > 5 ? ` (+${allErrors.length - 5} more)` : "";
            showSnack(display.join(" | ") + extra, "error");
            return;
        }

        setTotalRows(rows.length);
        setUploading(true);
        setProgress(0);
        setUploadedRows(0);
        setResults(null);

        const token = localStorage.getItem("u_token");
        const chunks = [];
        for (let i = 0; i < rows.length; i += CHUNK_SIZE) {
            chunks.push(rows.slice(i, i + CHUNK_SIZE));
        }

        let totalSuccess = 0;
        let totalFailed = 0;
        let processed = 0;

        for (let i = 0; i < chunks.length; i++) {
            const chunk_id = `chunk_${Date.now()}_${i + 1}`;

            try {
                const response = await fetch(`${Apiurl}admin/product/bulk-upload`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                        Accept: "*/*",
                    },
                    body: JSON.stringify({ chunk_id, products: chunks[i] }),
                });

                if (!response.ok) {
                    const errData = await response.json().catch(() => ({}));
                    throw new Error(errData?.message || errData?.error || `HTTP ${response.status}`);
                }

                const data = await response.json();

                if (data.success === false || data.status === false) {
                    throw new Error(data.message || data.error || "API returned failure");
                }

                totalSuccess += data.success_count ?? chunks[i].length;
                totalFailed += data.failed_count ?? 0;

            } catch (err) {
                setUploading(false);
                setProgress(0);
                setUploadedRows(0);
                setTotalRows(0);
                showSnack(
                    `Upload failed — ${err.message}. Upload stopped.`,
                    "error"
                );
                return;
            }

            processed += chunks[i].length;
            setUploadedRows(processed);
            setProgress(Math.round((processed / rows.length) * 100));
        }

        setUploading(false);
        setResults({ success: totalSuccess, failed: totalFailed });

        // final toast
        if (totalFailed === 0) {
            showSnack(`All ${totalSuccess} products uploaded successfully!`, "success");
        } else {
            showSnack(`Upload done — ${totalSuccess} succeeded, ${totalFailed} failed.`, "warning");
        }
    };

    const handleReset = () => {
        setFile(null);
        setProgress(0);
        setUploadedRows(0);
        setTotalRows(0);
        setResults(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    return (
        <>
            <Button
                variant="contained"
                startIcon={<UploadFileIcon />}
                onClick={handleOpen}
                sx={{
                    textTransform: "none",
                    bgcolor: "#2e7d32",
                    whiteSpace: "nowrap",
                    py: "2px",
                    px: "14px",
                    borderRadius: 12,
                    "&:hover": { bgcolor: "#1b5e20" },
                }}
            >
                Bulk Upload
            </Button>

            <Dialog
                open={open}
                onClose={handleClose}
                fullWidth
                maxWidth="sm"
                PaperProps={{ sx: { borderRadius: "12px", overflow: "hidden", boxShadow: "0 8px 40px rgba(0,0,0,0.14)" } }}
            >
                <DialogTitle sx={{ p: 0 }}>
                    <Box sx={{ background: "linear-gradient(135deg, #04121f 0%, #0a2035 100%)", px: 3.5, py: 2.5 }}>
                        <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "16px" }}>
                            Bulk Upload Products
                            <Box component="span" sx={{
                                ml: 1.5, fontSize: "10px", fontWeight: 700, letterSpacing: "0.06em",
                                textTransform: "uppercase", background: "rgba(255,255,255,0.12)",
                                color: "#b8cfff", borderRadius: "4px", px: 1, py: 0.3,
                            }}>Excel</Box>
                        </Typography>
                        <Typography sx={{ color: "#5a8aad", fontSize: "12px", mt: 0.3 }}>
                            Download the template, fill it in, then upload.
                        </Typography>
                    </Box>
                </DialogTitle>

                <DialogContent sx={{ px: 3.5, py: 3, backgroundColor: "#fff" }}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>

                        {/* Template download */}
                        <Box sx={{
                            display: "flex", alignItems: "center", justifyContent: "space-between",
                            p: 2, borderRadius: "10px", border: "1.5px solid #e2e6f0",
                            backgroundColor: "#f8f9fc", flexWrap: "wrap", gap: 1.5,
                        }}>
                            <Box>
                                <Typography sx={{ fontWeight: 700, fontSize: "13.5px", color: "#04121f" }}>
                                    Download Template
                                </Typography>
                                <Typography sx={{ fontSize: "12px", color: "#6b7a99", mt: 0.3 }}>
                                    All columns included with a sample row
                                </Typography>
                            </Box>
                            <Button
                                variant="outlined"
                                startIcon={<DownloadIcon />}
                                onClick={handleDownloadTemplate}
                                sx={{
                                    textTransform: "none", fontWeight: 600, fontSize: "13px",
                                    borderRadius: "8px", borderColor: "#04121f", color: "#04121f",
                                    "&:hover": { backgroundColor: "#f0f2f5", borderColor: "#04121f" },
                                }}
                            >
                                Template
                            </Button>
                        </Box>

                        {/* File picker */}
                        <Box>
                            <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#3d4a6b", mb: 0.8 }}>
                                Select Excel File
                            </Typography>
                            <Box
                                onClick={() => !uploading && fileInputRef.current?.click()}
                                sx={{
                                    border: "2px dashed", borderColor: file ? "#04121f" : "#d0d6e2",
                                    borderRadius: "10px", p: 3, textAlign: "center",
                                    cursor: uploading ? "not-allowed" : "pointer",
                                    backgroundColor: file ? "#f4f6fb" : "#fafbfc",
                                    transition: "all 0.2s",
                                    "&:hover": { borderColor: "#04121f", backgroundColor: "#f4f6fb" },
                                }}
                            >
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".xlsx,.xls"
                                    style={{ display: "none" }}
                                    onChange={handleFileChange}
                                />
                                {file ? (
                                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1.5 }}>
                                        <InsertDriveFileIcon sx={{ color: "#04121f", fontSize: 28 }} />
                                        <Box sx={{ textAlign: "left" }}>
                                            <Typography sx={{ fontWeight: 700, fontSize: "13px", color: "#04121f" }}>
                                                {file.name}
                                            </Typography>
                                            <Typography sx={{ fontSize: "11.5px", color: "#6b7a99" }}>
                                                {(file.size / 1024).toFixed(1)} KB · Click to change
                                            </Typography>
                                        </Box>
                                    </Box>
                                ) : (
                                    <>
                                        <UploadFileIcon sx={{ fontSize: 34, color: "#b0bbd8", mb: 0.5 }} />
                                        <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#3d4a6b" }}>
                                            Click to select your Excel file
                                        </Typography>
                                        <Typography sx={{ fontSize: "11.5px", color: "#9aa0b8", mt: 0.3 }}>
                                            .xlsx or .xls
                                        </Typography>
                                    </>
                                )}
                            </Box>
                        </Box>

                        {/* Progress */}
                        {uploading && (
                            <Box>
                                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.8 }}>
                                    <Typography sx={{ fontSize: "12.5px", fontWeight: 600, color: "#3d4a6b" }}>
                                        Uploading…
                                    </Typography>
                                    <Typography sx={{ fontSize: "12.5px", fontWeight: 700, color: "#04121f" }}>
                                        {uploadedRows} / {totalRows} rows · {progress}%
                                    </Typography>
                                </Box>
                                <LinearProgress
                                    variant="determinate"
                                    value={progress}
                                    sx={{
                                        height: 8, borderRadius: 4, backgroundColor: "#e2e6f0",
                                        "& .MuiLinearProgress-bar": {
                                            borderRadius: 4,
                                            background: "linear-gradient(90deg, #04121f, #1565c0)",
                                        },
                                    }}
                                />
                                <Typography sx={{ fontSize: "11.5px", color: "#9aa0b8", mt: 0.8 }}>
                                    Processing your request — please don't close this tab.
                                </Typography>
                            </Box>
                        )}

                        {/* Result */}
                        {results && (
                            <Box sx={{
                                p: 2, borderRadius: "8px",
                                backgroundColor: results.failed === 0 ? "#f0faf4" : "#fff8f0",
                                border: `1.5px solid ${results.failed === 0 ? "#a8dbb8" : "#f5c18a"}`,
                            }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                                    {results.failed === 0
                                        ? <CheckCircleIcon sx={{ color: "#2e7d52", fontSize: 20 }} />
                                        : <ErrorOutlineIcon sx={{ color: "#e65100", fontSize: 20 }} />}
                                    <Typography sx={{ fontWeight: 700, fontSize: "13.5px", color: results.failed === 0 ? "#2e7d52" : "#e65100" }}>
                                        {results.failed === 0 ? "Upload complete!" : "Upload finished with some errors"}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                                    <Chip label={`${results.success} succeeded`} size="small"
                                        sx={{ backgroundColor: "#d4edda", color: "#1a5c32", fontWeight: 700, fontSize: "12px" }} />
                                    {results.failed > 0 && (
                                        <Chip label={`${results.failed} failed`} size="small"
                                            sx={{ backgroundColor: "#ffe0cc", color: "#b84200", fontWeight: 700, fontSize: "12px" }} />
                                    )}
                                </Box>
                            </Box>
                        )}

                    </Box>
                </DialogContent>

                <DialogActions sx={{ px: 3.5, py: 2, backgroundColor: "#f8f9fc", borderTop: "1px solid #eef0f6", gap: 1 }}>
                    {(file || results) && (
                        <Button onClick={handleReset} disabled={uploading}
                            sx={{
                                textTransform: "none", fontWeight: 600, color: "#6b7a99",
                                border: "1.5px solid #d8dce8", borderRadius: "7px", px: 2.5,
                                "&:hover": { backgroundColor: "#eef0f6" },
                            }}
                        >Reset</Button>
                    )}
                    <Button onClick={handleClose} disabled={uploading}
                        sx={{
                            textTransform: "none", fontWeight: 600, color: "#6b7a99",
                            border: "1.5px solid #d8dce8", borderRadius: "7px", px: 2.5,
                            "&:hover": { backgroundColor: "#eef0f6" },
                        }}
                    >Close</Button>
                    <Button
                        variant="contained"
                        onClick={handleUpload}
                        disabled={!file || uploading}
                        startIcon={<UploadFileIcon />}
                        sx={{
                            textTransform: "none", fontWeight: 600, fontSize: "13px",
                            borderRadius: "8px", px: 3,
                            background: "linear-gradient(135deg, #04121f, #0d2d47)",
                            boxShadow: "0 2px 8px rgba(4,18,31,0.3)",
                            "&:hover": { background: "linear-gradient(135deg, #0a2035, #163d5e)" },
                            "&.Mui-disabled": { background: "#d0d6e2", color: "#9aa0b8" },
                        }}
                    >
                        {uploading ? `Uploading… ${progress}%` : "Upload Products"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Snackbar — renders outside Dialog so focus trap can't block it ── */}
            <Snackbar
                open={snack.open}
                autoHideDuration={6000}
                onClose={() => setSnack((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setSnack((s) => ({ ...s, open: false }))}
                    severity={snack.severity}
                    variant="filled"
                    sx={{ width: "100%", fontSize: "13px", borderRadius: "8px" }}
                >
                    {snack.message}
                </Alert>
            </Snackbar>
        </>
    );
}